'use client'

import { trackCalendlyBooked, trackCalendlyClick } from '@/utils/tracking'
import { usePathname } from 'next/navigation'
import Script from 'next/script'
import { ReactNode, useEffect } from 'react'

interface CalendlyEmbedProps {
  url?: string
  variant?: 'inline' | 'popup'
  text?: string
  color?: string
  textColor?: string
  className?: string
  prefill?: {
    name?: string
    email?: string
    customAnswers?: {
      a1?: string
      a2?: string
      a3?: string
    }
  }
  icon?: ReactNode
  /** Herkunft für Analytics-Events, z.B. 'floating-cta'; Default ist der Seitenpfad. */
  trackingSource?: string
  /**
   * Eigenes Button-Markup für variant="popup". Wird anstelle von icon/text
   * gerendert, damit Aufrufer ihr Layout behalten können, ohne auf einen
   * externen <a target="_blank"> auszuweichen (dort greift der
   * event_scheduled-Listener nicht, die Buchung wäre nicht messbar).
   */
  children?: ReactNode
}

/**
 * Der Listener für die Buchungs-Conversion liegt bewusst MODULWEIT und nicht in
 * der Komponente. Vorher registrierte jede gemountete CalendlyEmbed-Instanz
 * ihren eigenen window-message-Listener. Auf einer Landingpage sind das bis zu
 * vier (CTACard, Alternativ-Link im Formular, Erfolgs-Ansicht, FloatingCTA),
 * eine einzige Buchung hat damit vier Conversions an Google Ads gemeldet.
 * Nachgewiesen im E2E-Test am 02.09.2026.
 */
let listenerRefCount = 0
let detachListener: (() => void) | null = null
/** Herkunft der zuletzt geöffneten Buchungsstrecke, für die Analytics-Zuordnung. */
let activeSource = ''

function attachBookingListener() {
  listenerRefCount += 1
  if (detachListener) return

  const handleMessage = (e: MessageEvent) => {
    // Origin-Prüfung ist die Sicherheitsgrenze: ohne sie könnte jede Seite im
    // selben Tab eine Conversion auslösen.
    if (e.origin !== 'https://calendly.com') return
    if (e.data?.event !== 'calendly.event_scheduled') return
    trackCalendlyBooked(activeSource || window.location.pathname)
  }

  window.addEventListener('message', handleMessage)
  detachListener = () => window.removeEventListener('message', handleMessage)
}

function releaseBookingListener() {
  listenerRefCount = Math.max(0, listenerRefCount - 1)
  if (listenerRefCount === 0 && detachListener) {
    detachListener()
    detachListener = null
  }
}

export default function CalendlyEmbed({
  url = 'https://calendly.com/kommers/meet',
  variant = 'inline',
  text = 'Termin vereinbaren',
  color = '#ff6b35',
  className = '',
  prefill,
  icon,
  trackingSource,
  children
}: CalendlyEmbedProps) {
  const pathname = usePathname()
  const source = trackingSource || pathname

  // Calendly meldet eine abgeschlossene Buchung per postMessage aus dem iframe,
  // das ist die eigentliche Conversion, nicht der CTA-Klick. Der Listener wird
  // modulweit gehalten und hier nur referenzgezählt an- und abgemeldet.
  useEffect(() => {
    attachBookingListener()
    // Das eingebettete Widget ist die Buchungsstrecke, sobald es sichtbar ist.
    // Beim Popup setzt erst der Klick die Herkunft.
    if (variant === 'inline') activeSource = source
    return releaseBookingListener
  }, [source, variant])

  const handlePopupClick = (e: React.MouseEvent) => {
    e.preventDefault()
    activeSource = source
    trackCalendlyClick(source)
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url,
        prefill,
        pageSettings: {
          backgroundColor: 'ffffff',
          hideEventTypeDetails: false,
          hideLandingPageDetails: false,
          primaryColor: color,
          textColor: '#4d5055'
        }
      })
    }
  }

  if (variant === 'popup') {
    return (
      <>
        <Script src="https://assets.calendly.com/assets/external/widget.js" strategy="lazyOnload" />
        <link href="https://assets.calendly.com/assets/external/widget.css" rel="stylesheet" />
        <button
          onClick={handlePopupClick}
          type="button"
          className={children ? className : className || `bg-accent text-white px-8 py-4 rounded-3xl font-medium hover:bg-accent/90 transition-opacity`}
        >
          {children ?? (
            <>
              {icon} {text}
            </>
          )}
        </button>
      </>
    )
  }

  if (variant === 'inline') {
    return (
      <>
        <Script src="https://assets.calendly.com/assets/external/widget.js" strategy="lazyOnload" />
        <div
          className={`calendly-inline-widget ${className}`}
          data-url={`${url}?background_color=1a1a1a&text_color=ffffff&primary_color=${color.replace('#', '')}`}
          style={{ minWidth: '320px', height: '700px' }}
        />
      </>
    )
  }
  return null
}

// Add TypeScript declaration for Calendly
interface CalendlyOptions {
  url: string
  text?: string
  color?: string
  textColor?: string
  branding?: boolean
  prefill?: {
    name?: string
    email?: string
    customAnswers?: {
      a1?: string
      a2?: string
      a3?: string
    }
  }
  pageSettings?: {
    backgroundColor?: string
    hideEventTypeDetails?: boolean
    hideLandingPageDetails?: boolean
    primaryColor?: string
    textColor?: string
  }
  parentElement?: HTMLElement
}

declare global {
  interface Window {
    Calendly: {
      initBadgeWidget: (options: CalendlyOptions) => void
      initPopupWidget: (options: CalendlyOptions) => void
      initInlineWidget: (options: CalendlyOptions) => void
    }
  }
}
